import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import "./BrandFontDetails.scss";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import { getFontUrl } from "../../../../utils/helpers/common";
import Loading from "../../../loading/Loading";
import { clientRoutes } from "../../../../utils/routes";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { FontApi } from "../../../../utils/api/Font";
import BrandFontActionMenu from "./BrandFontActionMenu";
import { CircularProgress, Modal } from "@mui/material";

type ConfirmType = "activate" | "deactivate" | "delete" | "delete-variant";

const BrandFontDetails = () => {
  const navigate = useNavigate();
  const { font_id } = useParams<{ font_id: string }>();

  const [isLoading, setIsLoading] = useState(true);
  const [fontData, setFontData] = useState<any>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<ConfirmType | null>(null);
  const [confirmVariantId, setConfirmVariantId] = useState<string | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const previewText = "The quick brown fox jumps over the lazy dog";

  const fetchDetails = async () => {
    if (!font_id) return;
    setIsLoading(true);

    const res = await FontApi.getBrandFontById(font_id);

    if (res?.success && res?.data?.success) {
      setFontData(res.data.data);
      injectAllFontFaces(res.data.data);
    } else {
      toast.error(res?.data?.message || "Failed to load font details");
    }
    setIsLoading(false);
  };

  const injectAllFontFaces = (data: any) => {
    if (!data) return;

    const mainUrl = getFontUrl(data as any);
    if (mainUrl && !mainUrl.includes("pending")) {
      loadFont(`brand-font-main-${data._id}`, mainUrl, "400", false);
    }

    if (Array.isArray(data.variants)) {
      data.variants.forEach((v: any) => {
        const vUrl = getFontUrl(v as any);
        if (!vUrl || vUrl.includes("pending")) return;
        loadFont(
          `brand-font-variant-${v._id}`,
          vUrl,
          v.font_weight || "400",
          !!v.is_italic,
        );
      });
    }
  };

  const loadFont = async (
    family: string,
    url: string,
    weight: string,
    italic: boolean,
  ) => {
    try {
      const alreadyLoaded = Array.from(document.fonts).some(
        (f) => f.family === family,
      );
      if (alreadyLoaded) return;

      const face = new FontFace(family, `url(${url})`, {
        weight,
        style: italic ? "italic" : "normal",
        display: "swap",
      });

      const loaded = await face.load();
      document.fonts.add(loaded);
    } catch (err) {
      console.warn(`Failed loading font ${family}`, err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    const datePart = d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hourStr = hours.toString().padStart(2, "0");
    return `${datePart}, ${hourStr}:${minutes} ${ampm}`;
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmType(null);
    setConfirmVariantId(null);
  };

  const getConfirmConfig = (type: ConfirmType) => {
    switch (type) {
      case "activate":
        return {
          title: "Activate font?",
          lines: [
            "Are you sure you want to activate this font?",
            "Once activated, this font will be available for use across the platform.",
          ],
          note: "",
          confirmText: "Confirm",
          confirmVariant: "dark" as const,
        };
      case "deactivate":
        return {
          title: "Deactivate font?",
          lines: [
            "Are you sure you want to deactivate this font?",
            "This font will no longer be available for use until reactivated.",
          ],
          note: "",
          confirmText: "Deactivate",
          confirmVariant: "danger" as const,
        };
      case "delete":
        return {
          title: "Delete font?",
          lines: [
            "Are you sure you want to delete this font family?",
            "This will permanently remove the font and all its variants from the system.",
          ],
          note: "This action cannot be undone.",
          confirmText: "Delete",
          confirmVariant: "danger" as const,
        };
      case "delete-variant":
        return {
          title: "Delete variant?",
          lines: [
            "Are you sure you want to delete this variant?",
            "This variant will be permanently removed from the font family.",
          ],
          note: "This action cannot be undone.",
          confirmText: "Delete",
          confirmVariant: "danger" as const,
        };
    }
  };

  const handleAction = (id: string, action: string) => {
    if (!id) return;

    if (action === "delete") {
      setConfirmType("delete");
      setConfirmOpen(true);
      return;
    }

    if (action === "activate" || action === "deactivate") {
      setConfirmType(action as ConfirmType);
      setConfirmOpen(true);
      return;
    }
  };

  const handleDeleteVariant = (variantId: string) => {
    if (!variantId) return;
    setConfirmVariantId(variantId);
    setConfirmType("delete-variant");
    setConfirmOpen(true);
  };

  const executeAction = async () => {
    if (!confirmType || !fontData?._id) return;

    setConfirmLoading(true);
    try {
      if (confirmType === "delete") {
        const res = await FontApi.deleteBrandFont(fontData._id);
        if (res?.success && res?.data?.success) {
          toast.success(res.data.message || "Font deleted successfully");
          closeConfirm();
          navigate(clientRoutes.brandFonts);
          return;
        }
        toast.error(res?.data?.message || "Failed to delete font");
        return;
      }

      if (confirmType === "delete-variant" && confirmVariantId) {
        const res = await FontApi.deleteBrandFontVariant(confirmVariantId);
        if (res?.success && res?.data?.success) {
          toast.success(res.data.message || "Variant deleted successfully");
          closeConfirm();
          fetchDetails();
          return;
        }
        toast.error(res?.data?.message || "Failed to delete variant");
        return;
      }

      const newStatus = confirmType === "activate" ? "active" : "disabled";
      const res = await FontApi.updateBrandFontStatus(fontData._id, newStatus);
      if (res?.success && res?.data?.success) {
        toast.success(res.data.message || "Status updated successfully");
        closeConfirm();
        fetchDetails();
        return;
      }
      toast.error(res?.data?.message || "Failed to update status");
    } finally {
      setConfirmLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [font_id]);

  if (isLoading) {
    return (
      <ProtectedComponent>
        <NavigationLayout>
          <div className="brand-font-details-loading">
            <Loading />
          </div>
        </NavigationLayout>
      </ProtectedComponent>
    );
  }

  if (!fontData) {
    return (
      <ProtectedComponent>
        <NavigationLayout>
          <div className="brand-font-details-empty">
            <p>Font not found</p>
          </div>
        </NavigationLayout>
      </ProtectedComponent>
    );
  }

  const normalVariants = (fontData.variants || []).filter(
    (v: any) => !v.is_italic,
  );
  const italicVariants = (fontData.variants || []).filter(
    (v: any) => v.is_italic,
  );

  const brandName =
    fontData?.brand?.brand_name || fontData?.brand?.email || "-";

  const mainFontFamily = `brand-font-main-${fontData._id}`;

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="brand-font-details-wrapper">
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate(clientRoutes.brandFonts)}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to Brand fonts list</p>
          </div>

          <div className="details-header">
            <div className="header-row">
              <h2 className="details-title">Brand font info</h2>
              <div className="action-icon-btn">
                <BrandFontActionMenu
                  fontId={fontData._id}
                  status={fontData.status}
                  onAction={handleAction}
                  canEdit={true}
                />
              </div>
            </div>

            <div className="metadata-grid">
              <div className="meta-item">
                <label>Brand font id</label>
                <span className="meta-value">{fontData._id}</span>
              </div>
              <div className="meta-item">
                <label>Font family</label>
                <span className="meta-value">{fontData.font_family}</span>
              </div>
              <div className="meta-item">
                <label>Brand</label>
                <span className="meta-value">{brandName}</span>
              </div>
              <div className="meta-item">
                <label>Brand id</label>
                <span
                  className="meta-value brand-id-link"
                  onClick={() => {
                    if (fontData?.brand?._id) {
                      window.open(
                        `/brand/view/${fontData.brand._id}`,
                        "_blank",
                      );
                    }
                  }}
                >
                  {fontData?.brand?._id || "-"}
                </span>
              </div>

              <div className="meta-item">
                <label>Status</label>
                <span
                  className={`status-badge ${
                    fontData.status === "active" ? "active" : "inactive"
                  }`}
                >
                  {fontData.status?.charAt(0).toUpperCase() +
                    fontData.status?.slice(1)}
                </span>
              </div>
              <div className="meta-item">
                <label>Created On</label>
                <span className="meta-value">
                  {formatDate(fontData.createdAt)}
                </span>
              </div>
              <div className="meta-item">
                <label>Last Updated On</label>
                <span className="meta-value">
                  {formatDate(fontData.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="main-preview-section">
            <p className="section-label">Preview</p>
            <p
              className="main-preview-text"
              style={{
                fontFamily: `'${mainFontFamily}', '${fontData.font_family}', inter`,
              }}
            >
              {previewText}
            </p>
          </div>

          {normalVariants.length > 0 && (
            <div className="variant-section">
              <p className="variant-section-title">Normal font weights</p>
              <div className="variant-cards-grid">
                {normalVariants.map((v: any) => (
                  <VariantCard
                    key={v._id}
                    variant={v}
                    fontFamily={fontData.font_family}
                    onDelete={handleDeleteVariant}
                  />
                ))}
              </div>
            </div>
          )}

          {italicVariants.length > 0 && (
            <div className="variant-section">
              <p className="variant-section-title">Italic font weights</p>
              <div className="variant-cards-grid">
                {italicVariants.map((v: any) => (
                  <VariantCard
                    key={v._id}
                    variant={v}
                    fontFamily={fontData.font_family}
                    onDelete={handleDeleteVariant}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <Modal
          open={confirmOpen}
          onClose={closeConfirm}
          className="font-confirm-modal"
          disableEscapeKeyDown={confirmLoading}
        >
          <div className="fcm-center" onClick={(e) => e.stopPropagation()}>
            <div className="fcm-card">
              {confirmType && (
                <>
                  {(() => {
                    const cfg = getConfirmConfig(confirmType);
                    return (
                      <>
                        <p className="fcm-title">{cfg.title}</p>

                        <div className="fcm-body">
                          {cfg.lines.map((t, idx) => (
                            <p key={idx} className="fcm-text">
                              {t}
                            </p>
                          ))}

                          {cfg.note ? (
                            <div className="fcm-note">
                              <p className="fcm-note-title">Note:</p>
                              <p className="fcm-note-text">{cfg.note}</p>
                            </div>
                          ) : null}
                        </div>

                        <div className="fcm-actions">
                          <button
                            className="fcm-cancel"
                            onClick={closeConfirm}
                            disabled={confirmLoading}
                            type="button"
                          >
                            Cancel
                          </button>

                          <button
                            className={`fcm-confirm ${
                              cfg.confirmVariant === "danger" ? "danger" : ""
                            }`}
                            onClick={executeAction}
                            disabled={confirmLoading}
                            type="button"
                          >
                            {confirmLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              cfg.confirmText
                            )}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </Modal>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default BrandFontDetails;

interface VariantCardProps {
  variant: any;
  fontFamily: string;
  onDelete: (variantId: string) => void;
}

const VariantCard: React.FC<VariantCardProps> = ({
  variant,
  fontFamily,
  onDelete,
}) => {
  const previewText = "The quick brown fox jumps over the lazy dog";
  const variantFamily = `brand-font-variant-${variant._id}`;

  return (
    <div className="variant-card">
      <h3
        className="variant-name"
        style={{
          fontFamily: `'${variantFamily}', '${fontFamily}', sans-serif`,
          fontStyle: variant.is_italic ? "italic" : "normal",
          fontWeight: variant.font_weight || 400,
        }}
      >
        {fontFamily}
      </h3>
      <p className="variant-id">id: {variant._id}</p>

      <div className="variant-info-rows">
        <div className="info-row">
          <span className="info-label">Full font name</span>
          <span className="info-value">
            {variant.postscript_name?.replace(/-/g, " ") ||
              variant.style_name ||
              "-"}
          </span>
        </div>
        <div className="info-row">
          <span className="info-label">PostScript name</span>
          <span className="info-value">{variant.postscript_name || "-"}</span>
        </div>
        <div className="info-row">
          <span className="info-label">Font weight</span>
          <span className="info-value">{variant.font_weight || "-"}</span>
        </div>
      </div>

      <p
        className="variant-preview-text"
        style={{
          fontFamily: `'${variantFamily}', '${fontFamily}', sans-serif`,
          fontStyle: variant.is_italic ? "italic" : "normal",
          fontWeight: variant.font_weight || 400,
        }}
      >
        {previewText}
      </p>

      <button
        className="variant-delete-btn"
        onClick={() => onDelete(variant._id)}
      >
        Delete
      </button>
    </div>
  );
};
